import React, { useState } from 'react';
import { Settings, Activity, Users, Database } from 'lucide-react';
import { motion } from 'framer-motion';
import AdminPanel from './AdminPanel';
import AdminAnalytics from './AdminAnalytics';

const AdminMain = ({ onAuthenticationChange }) => {
  const [activeTab, setActiveTab] = useState('panel');
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Function to handle authentication state changes from AdminPanel
  const handleAuthenticationChange = (authState) => {
    setIsAuthenticated(authState);
    if (onAuthenticationChange) {
      onAuthenticationChange(authState);
    }
    if (!authState) {
      // Reset to panel tab if logged out
      setActiveTab('panel');
    }
  };

  const tabs = [
    { id: 'panel', name: 'Admin Panel', icon: Settings, component: AdminPanel },
    ...(isAuthenticated ? [{ id: 'analytics', name: 'Analytics & Reports', icon: Activity, component: AdminAnalytics }] : [])
  ];

  const ActiveComponent = tabs.find(tab => tab.id === activeTab)?.component || AdminPanel;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-gray-900 to-slate-900">
      {/* Header section - shows slightly different copy depending on authentication, but AdminPanel is mounted in a single place to avoid remount flicker */}
      <div className="relative">
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fillRule='evenodd'%3E%3Cg fill='%23ffffff' fillOpacity='0.03'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }}></div>

        <div className="relative max-w-7xl mx-auto px-4 py-2 text-center">
          <motion.div 
            className="inline-flex items-center px-2 py-1 bg-white/10 backdrop-blur-sm rounded-full border border-white/20 mb-1"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Settings className="w-3 h-3 text-orange-400 mr-2" />
            <span className="text-white font-medium text-xs">{isAuthenticated ? 'Admin Dashboard' : 'Admin Portal'}</span>
          </motion.div>

          <h1 className="text-2xl lg:text-3xl font-bold text-white mb-1">
            Admin <span className="bg-gradient-to-r from-orange-400 via-red-400 to-pink-400 bg-clip-text text-transparent">Dashboard</span>
          </h1>

          <p className="text-sm text-white/80 max-w-xl mx-auto">
            {isAuthenticated ? 'Comprehensive analytics, user tracking, and system management' : 'Secure access to comprehensive analytics, user tracking, and system management'}
          </p>
        </div>
      </div>

      {/* Navigation shown only when authenticated */}
      {isAuthenticated && (
        <div className="bg-white border-b">
          <div className="max-w-7xl mx-auto px-4">
            <nav className="flex space-x-8">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center px-4 py-4 border-b-2 font-medium text-sm transition-colors ${
                      activeTab === tab.id
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Icon className="w-5 h-5 mr-2" />
                    {tab.name}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>
      )}

      {/* Main content area - AdminPanel is mounted in one place to avoid flicker during auth transitions */}
      <div className="bg-gray-50 min-h-screen">
        {activeTab === 'panel' ? (
          <div className={isAuthenticated ? '' : 'bg-white'}>
            <div className={`flex justify-center px-4 py-8 ${isAuthenticated ? '' : ''}`}>
              <AdminPanel onAuthenticationChange={handleAuthenticationChange} isAuthenticated={isAuthenticated} showBackground={isAuthenticated} />
            </div>
          </div>
        ) : (
          <ActiveComponent />
        )}
      </div>
    </div>
  );
};

export default AdminMain;